import { cn } from '@lib/util/cn';
import { hastNodeText, slugifyHeading } from '@lib/util/typography';
import Link from 'next/link';
import type { Components, ExtraProps } from 'react-markdown';

// Pure rendering pieces shared between `MarkdownContent` (Client Component —
// used by interactive surfaces like the consultation flow) and
// `MarkdownContentServer` (Server Component — used by static content pages).
// Nothing here uses hooks or browser APIs, so it's safe to import from either.

type HTag = 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

function withId(Tag: HTag) {
  return ({
    children,
    node,
    id: propsId,
    ...props
  }: React.HTMLAttributes<HTMLHeadingElement> & ExtraProps) => {
    const id =
      typeof propsId === 'string'
        ? propsId
        : slugifyHeading(hastNodeText(node));
    return (
      <Tag id={id} {...props}>
        {children}
      </Tag>
    );
  };
}

const hLevels: HTag[] = ['h2', 'h3', 'h4', 'h5', 'h6'];

// h1 in markdown always maps to h2 — avoids multiple h1s on the page.
// Shifted (default): ##→h3, ###→h4; Natural: ##→h2, ###→h3
export const headingComponentsShifted: Components = {
  h1: withId('h2'),
  h2: withId(hLevels[1]),
  h3: withId(hLevels[2]),
  h4: withId(hLevels[3]),
  h5: withId(hLevels[4]),
  h6: withId('h6'),
};

export const headingComponentsNatural: Components = {
  h1: withId('h2'),
  h2: withId(hLevels[0]),
  h3: withId(hLevels[1]),
  h4: withId(hLevels[2]),
  h5: withId(hLevels[3]),
  h6: withId('h6'),
};

export const markdownElementComponents: Components = {
  a: ({ href, ...props }) =>
    href?.startsWith('/') ? (
      <Link href={href} {...props} />
    ) : (
      <a href={href} target="_blank" rel="noopener noreferrer" {...props} />
    ),
  table: ({ children, ...props }) => (
    <section className="overflow-x-auto" aria-label="Scrollable table">
      <table {...props}>{children}</table>
    </section>
  ),
  th: ({ children, ...props }) => (
    <th style={{ minWidth: '250px', verticalAlign: 'top' }} {...props}>
      {children}
    </th>
  ),
  td: ({ children, ...props }) => (
    <td style={{ minWidth: '250px' }} {...props}>
      {children}
    </td>
  ),
};

export function markdownProseClassName({
  compact = false,
  inheritTextColor = false,
  className,
}: {
  compact?: boolean;
  inheritTextColor?: boolean;
  className?: string;
} = {}) {
  const baseProseClasses = [
    'prose',
    'prose-headings:text-ui-fg-base',
    'prose-p:text-ui-fg-subtle',
    'prose-ul:text-ui-fg-subtle prose-ol:text-ui-fg-subtle',
    'prose-li:text-ui-fg-subtle [&_li::marker]:text-ui-fg-subtle',
    'prose-th:text-ui-fg-base',
    'prose-td:text-ui-fg-subtle',
    'prose-strong:text-ui-fg-base',
  ];

  return cn(
    !inheritTextColor && baseProseClasses,
    'max-w-none',
    'prose-headings:font-heading prose-headings:font-bold',
    'prose-p:leading-relaxed prose-p:text-pretty',
    'prose-a:text-fg-interactive-strong prose-a:underline prose-a:underline-offset-2 prose-a:decoration-fg-interactive-strong/40 [&_a:hover]:decoration-fg-interactive-strong',
    'prose-table:text-sm prose-table:my-2.5',
    'prose-th:text-left prose-th:font-semibold',
    'prose-strong:font-semibold',
    compact && 'prose-sm prose-p:my-1 prose-headings:my-2',
    !compact && 'prose-base',
    className,
  );
}
